import { useEffect } from 'react'
import type { ChartManager } from '../core/ChartManager'

/** Quiet period after the last size change before the settle pass runs. Long
 *  enough to outlast a mosaic drag's transient collapse/expand flip-flops,
 *  short enough that the healed axis appears immediately after drop. */
const SETTLE_MS = 200

/**
 * Attaches a ResizeObserver to the chart container and calls manager.resize()
 * on size changes. Debounced via requestAnimationFrame to prevent rapid-fire
 * recalculations during window resize drags.
 *
 * A trailing debounce additionally fires ONE `settleResize()` once the storm
 * goes quiet — a mosaic drag transiently collapses the container and
 * klinecharts freezes the y-axis range computed in that collapsed state (and
 * can clamp away the right-edge gap); the settle pass re-runs the layout at
 * the final size and restores the gap. See ChartManager.settleResize.
 */
export function useChartResize(
  chartContainerRef: React.RefObject<HTMLDivElement | null>,
  managerRef: React.RefObject<ChartManager | null>,
): void {
  useEffect(() => {
    if (!chartContainerRef.current) return
    let rafId: number
    let settleRafId: number
    let settleId: ReturnType<typeof setTimeout> | undefined
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => managerRef.current?.resize())
      clearTimeout(settleId)
      settleId = setTimeout(() => managerRef.current?.settleResize(), SETTLE_MS)
    })
    ro.observe(chartContainerRef.current)
    // A mosaic panel drop ends with a document-level HTML5 dragend/drop.
    // klinecharts' first post-drop layout does NOT recompute the y-axis range
    // (verified in a live trace: the pre-drag range is carried ~stale until a
    // second resize runs) — so settle on the very next frame after the drop
    // reflow instead of waiting out the debounce, or the corrected axis paints
    // ~200ms late and reads as a visible jump. Non-mosaic drags (rows, files)
    // also land here; a settle on a healthy chart recomputes the same range —
    // a visual no-op, so no gating is needed.
    const onDragEnd = () => {
      clearTimeout(settleId)
      cancelAnimationFrame(settleRafId)
      settleRafId = requestAnimationFrame(() => managerRef.current?.settleResize())
    }
    document.addEventListener('dragend', onDragEnd, true)
    document.addEventListener('drop', onDragEnd, true)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(rafId)
      cancelAnimationFrame(settleRafId)
      clearTimeout(settleId)
      document.removeEventListener('dragend', onDragEnd, true)
      document.removeEventListener('drop', onDragEnd, true)
    }
  }, [chartContainerRef, managerRef])
}
